import { useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function usePackerTaskConfig() {
  const { api, AdminId } = useGlobalContext();

  /* ============================================================
     CONFIG DETAILS API
     FlagId 1 = Insert
     FlagId 2 = Get
     FlagId 3 = Update
  ============================================================ */

  const callPackerTaskConfigApi = useCallback(
    async (flagId, payload, showSuccess = false) => {
      try {
        const response = await api.post(
          "/api/PackerTaskControoler/v1.0/configDetails",
          payload,
          {
            headers: {
              FlagId: String(flagId),
              "ADMIN-ID": String(AdminId),
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        if (responseData?.responseCode === "00") {
          if (showSuccess) {
            toast.success(
              responseData?.responseMessage ||
                "Operation performed successfully",
            );
          }

          return {
            success: true,
            data: Array.isArray(responseData?.data) ? responseData.data : [],
          };
        }

        // Data not found case mein error toast nahi dikhayenge
        if (
          responseData?.responseCode === "02" ||
          responseData?.responseCode === "03"
        ) {
          return {
            success: true,
            data: [],
          };
        }

        toast.error(
          responseData?.responseMessage || "Configuration operation failed",
        );

        return {
          success: false,
          data: [],
        };
      } catch (error) {
        console.error("Packer task config API error:", error);

        toast.error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Something went wrong",
        );

        return {
          success: false,
          data: [],
        };
      }
    },
    [api, AdminId],
  );

  /* ================= GET CONFIG - FLAG ID 2 ================= */

  const getPackerTaskConfig = useCallback(
    async (driverId) => {
      const normalizedDriverId = String(driverId || "").trim();

      if (!normalizedDriverId) {
        return null;
      }

      const result = await callPackerTaskConfigApi(2, {
        driverId: normalizedDriverId,
      });

      return result.success ? result.data?.[0] || null : null;
    },
    [callPackerTaskConfigApi],
  );

  /* ================= SAVE CONFIG - FLAG ID 1 / 3 ================= */

  const savePackerTaskConfig = useCallback(
    async (payload, isEdit = false) => {
      const normalizedPayload = {
        driverId: String(payload?.driverId || "").trim(),
        dailyTaskLimit: String(payload?.dailyTaskLimit ?? "").trim(),
        concurrentOrder: String(payload?.concurrentOrder ?? "").trim(),
        maxTeamLimit: String(payload?.maxTeamLimit ?? "").trim(),
        cashLimit: String(payload?.cashLimit ?? "").trim(),
      };

      if (!normalizedPayload.driverId) {
        toast.error("Please select a driver");
        return false;
      }

      if (!normalizedPayload.dailyTaskLimit) {
        toast.error("Daily task limit is required");
        return false;
      }

      if (!normalizedPayload.concurrentOrder) {
        toast.error("Concurrent order is required");
        return false;
      }

      if (!normalizedPayload.maxTeamLimit) {
        toast.error("Maximum team limit is required");
        return false;
      }

      if (!normalizedPayload.cashLimit) {
        toast.error("Cash limit is required");
        return false;
      }

      const flagId = isEdit ? 3 : 1;

      const result = await callPackerTaskConfigApi(
        flagId,
        normalizedPayload,
        true,
      );

      return result.success;
    },
    [callPackerTaskConfigApi],
  );

  /* ============================================================
     GET ALL PACKER TASKS
     Status hamesha static ["all"] jayega
  ============================================================ */

  const getAllPackerTask = useCallback(
    async ({ driverId = "", fromDate = "", toDate = "" } = {}) => {
      const payload = {
        driverId: String(driverId || "").trim(),
        fromDate: String(fromDate || "").trim(),
        toDate: String(toDate || "").trim(),

        // Static status
        status: ["all"],
      };

      try {
        const response = await api.post(
          "/api/PackerTaskControoler/v1.0/getAllPackerTask",
          payload,
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        if (responseData?.responseCode === "00") {
          return Array.isArray(responseData?.data) ? responseData.data : [];
        }

        if (
          responseData?.responseCode === "02" ||
          responseData?.responseCode === "03"
        ) {
          return [];
        }

        toast.error(
          responseData?.responseMessage || "Failed to fetch packer tasks",
        );

        return [];
      } catch (error) {
        console.error("getAllPackerTask error:", error);

        toast.error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Error fetching packer tasks",
        );

        return [];
      }
    },
    [api],
  );

  /* ============================================================
   UPDATE DRIVER PACKER STATUS
   status: "Y" or "N"
============================================================ */

  const updateDriverPackerStatus = useCallback(
    async (driverId, status) => {
      const normalizedDriverId = String(driverId || "").trim();
      const normalizedStatus = String(status || "")
        .trim()
        .toUpperCase();

      if (!normalizedDriverId) {
        toast.error("Driver ID is required");
        return false;
      }

      if (!["Y", "N"].includes(normalizedStatus)) {
        toast.error("Status must be Y or N");
        return false;
      }

      const payload = {
        adminId: String(AdminId || "").trim(),
        driverId: normalizedDriverId,
        status: normalizedStatus,
      };

      try {
        const response = await api.post(
          "/api/Driver/v1.0/updateDriverPackerStatus",
          payload,
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        if (responseData?.responseCode === "00") {
          toast.success(
            responseData?.responseMessage ||
              (normalizedStatus === "Y"
                ? "Driver enabled as packer"
                : "Driver removed as packer"),
          );

          return true;
        }

        toast.error(
          responseData?.responseMessage ||
            "Failed to update driver packer status",
        );

        return false;
      } catch (error) {
        console.error("updateDriverPackerStatus error:", error);

        toast.error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Error updating driver packer status",
        );

        return false;
      }
    },
    [api, AdminId],
  );

 return {
   getPackerTaskConfig,
   savePackerTaskConfig,
   getAllPackerTask,
   updateDriverPackerStatus,
 };
}
